'use client'

import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  MAP_V2_DECOR,
  MAP_V2_DEFS,
  MAP_V2_PATHS,
  MAP_V2_STYLE,
  MAP_V2_VIEW_BOX,
  UB_DISTRICTS,
  UB_DISTRICT_SVG
} from '@/lib/mongolia-map-v2'
import { cn } from '@/lib/utils'

const CAPITAL = 'Улаанбаатар'

export type MongoliaMapProps = {
  selected: string | null
  onSelect: (province: string | null) => void

  /** Дарж болох аймгууд (өгөгдөлтэй нь) */
  selectable: Set<string>

  /** Аймаг бүрийн товч тайлбар — hover/дарахад гарна */
  labelOf?: (province: string) => string
}

/**
 * Монгол Улсын интерактив зураг (2-р хувилбар).
 *
 * Дизайнераас ирсэн `map.svg`-ийг задалж React болгов: аймгууд нь жинхэнэ
 * React элемент (дарж болно), градиент, цохилдог цэг, нэрийн бичиг нь
 * өөрчлөгддөггүй тул түүхий SVG-ээр шууд орно.
 *
 * ⚠️ Улаанбаатар нь ЖИЖИГ дүрс тул зөвхөн дүрс дээр нь дарах хэцүү.
 * Дизайнерын эх HTML-ийн адил нэр, цэгийг нь ч дарах талбар болгосон —
 * тэдгээр нь `pointer-events: none` тул JS-ээр буцааж асаана.
 */
export const MongoliaMap = ({ selectable, selected, onSelect, labelOf }: MongoliaMapProps) => {
  const [districtsOpen, setDistrictsOpen] = useState(false)
  const [district, setDistrict] = useState<string | null>(null)

  /*
   * Нийслэлийн нэр ба цэгийг дарж болдог болгоно. Тэдгээр нь чимэглэлийн
   * түүхий SVG дотор байдаг тул React-аар биш, DOM-оор л хүрч болно.
   */
  useEffect(() => {
    const host = document.getElementById('mn-map-v2')
    const shape = host?.querySelector<SVGPathElement>('[data-province="Улаанбаатар"]')

    if (!host || !shape) {
      return
    }

    const box = shape.getBBox()

    const inside = (x: number, y: number) =>
      x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height

    const proxies: SVGElement[] = []

    host.querySelectorAll<SVGGElement>('.pt').forEach(dot => {
      const transform = dot.parentElement?.getAttribute('transform') ?? ''
      const match = /translate\(\s*([-\d.]+)[\s,]+([-\d.]+)/.exec(transform)

      if (match && inside(parseFloat(match[1]), parseFloat(match[2]))) {
        proxies.push(dot)
      }
    })

    const open = () => setDistrictsOpen(true)

    proxies.forEach(el => {
      el.style.pointerEvents = 'auto'
      el.style.cursor = 'pointer'
      el.addEventListener('click', open)
    })

    return () => {
      proxies.forEach(el => {
        el.style.pointerEvents = ''
        el.removeEventListener('click', open)
      })
    }
  }, [])

  return (
    <>
      <svg
        id='mn-map-v2'
        viewBox={MAP_V2_VIEW_BOX}
        className='h-auto w-full'
        role='img'
        aria-label='Монгол Улсын аймгуудаар борлуулалтын хамрах хүрээ'
      >
        <style dangerouslySetInnerHTML={{ __html: MAP_V2_STYLE }} />
        <g dangerouslySetInnerHTML={{ __html: MAP_V2_DEFS }} />

        <g>
          {Object.entries(MAP_V2_PATHS).map(([name, d]) => {
            const clickable = name === CAPITAL || selectable.has(name)

            return (
              <path
                key={name}
                d={d}
                data-province={name}
                className={cn('aimag', selected === name && 'is-hover')}
                fill='url(#mapGrad)'
                stroke='#3C82C4'
                strokeOpacity={0.5}
                strokeLinecap='round'
                strokeLinejoin='round'
                tabIndex={clickable ? 0 : -1}
                role={clickable ? 'button' : undefined}
                aria-label={labelOf?.(name) ?? name}
                style={{ pointerEvents: clickable ? 'auto' : 'none' }}
                onClick={() => (name === CAPITAL ? setDistrictsOpen(true) : onSelect(selected === name ? null : name))}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (name === CAPITAL) setDistrictsOpen(true)
                    else onSelect(selected === name ? null : name)
                  }
                }}
              >
                <title>{labelOf?.(name) ?? name}</title>
              </path>
            )
          })}
        </g>

        <g dangerouslySetInnerHTML={{ __html: MAP_V2_DECOR }} />
      </svg>

      {/* Улаанбаатар дээр дарахад — дүүргийн зураг */}
      <Dialog open={districtsOpen} onOpenChange={setDistrictsOpen}>
        <DialogContent className='max-h-[85vh] overflow-y-auto p-8 sm:max-w-4xl'>
          <DialogHeader>
            <DialogTitle>Улаанбаатар</DialogTitle>
          </DialogHeader>

          <div className='grid gap-6 md:grid-cols-[minmax(0,1fr)_13rem]'>
            <div
              id='ub-districts'
              className='[&_.district]:cursor-pointer [&_svg]:h-auto [&_svg]:w-full'
              onClick={event => {
                const shape = (event.target as Element).closest('.district')

                setDistrict(shape?.getAttribute('data-name') ?? null)
              }}
              dangerouslySetInnerHTML={{ __html: UB_DISTRICT_SVG }}
            />

            <ul className='flex flex-col gap-1'>
              {UB_DISTRICTS.map(name => (
                <li key={name}>
                  <button
                    type='button'
                    onClick={() => setDistrict(name)}
                    className={cn(
                      'w-full rounded-lg px-3 py-2 text-start text-xl transition-colors',
                      district === name
                        ? 'bg-primary/15 text-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
